var _react_refresh_temp_1, _react_refresh_temp_2, _react_refresh_temp_3;
var _react_refresh_temp_4;
_react_refresh_temp_4 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ParticipantEvent, Track, } from 'livekit-client';
import SubtitleArea from './subtitleArea';
import { store, useAppDispatch, useAppSelector } from '../../store';
import { getAzureToken, openConnectionWithAzure, renewAzureToken, } from './helpers/apiConnections';
import { cleanAzureToken } from '../../store/slices/roomSettingsSlice';
import SelectOptions from './selectOptions';
import { updateSelectedSubtitleLang } from '../../store/slices/speechServicesSlice';
import SubtitleTextsHistory from './history';
const speechServiceFeaturesSelector = createSelector(_react_refresh_temp_1 = (state) => { var _a; return (_a = state.session.currentRoom.metadata) === null || _a === void 0 ? void 0 : _a.room_features; }, (room_features) => room_features === null || room_features === void 0 ? void 0 : room_features.speech_to_text_translation_features);
$RefreshReg$(_react_refresh_temp_1, "speechServiceFeaturesSelector$createSelector");
const azureTokenInfoSelector = createSelector(_react_refresh_temp_2 = (state) => state.roomSettings, (roomSettings) => roomSettings.azureTokenInfo);
$RefreshReg$(_react_refresh_temp_2, "azureTokenInfoSelector$createSelector");
const tokenRenewInterval = 8 * 60 * 1000;
const SpeechToTextService = ({ currentRoom }) => {
    _react_refresh_temp_4();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const speechService = useAppSelector(speechServiceFeaturesSelector);
    const azureTokenInfo = useAppSelector(azureTokenInfoSelector);
    const [speechLang, setSpeechLang] = useState('');
    const [recognizer, setRecognizer] = useState(undefined);
    const [deviceId, setDeviceId] = useState('');
    // this stream from livekit mic stream
    const [mediaStream, setMediaStream] = useState(undefined);
    // this one we had created from either device id or stream
    const [createdMediaStream, setCreatedMediaStream] = useState(undefined);
    const [optionSelectionDisabled, setOptionSelectionDisabled] = useState(false);
    const [isOpenPopover, setIsOpenPopover] = useState(false);
    // by default, we'll select the first language as default subtitle
    useEffect(() => {
        var _a;
        const selectedSubtitleLang = store.getState().speechServices.selectedSubtitleLang;
        if (isEmpty(selectedSubtitleLang)) {
            const defaultSubtitleLang = (_a = store.getState().session.currentRoom.metadata) === null || _a === void 0 ? void 0 : _a.room_features.speech_to_text_translation_features.default_subtitle_lang;
            if (defaultSubtitleLang && !isEmpty(defaultSubtitleLang)) {
                dispatch(updateSelectedSubtitleLang(defaultSubtitleLang));
            }
        }
    }, []);
    const handleUserMutedMic = useCallback((publication) => {
        if (!createdMediaStream) {
            return;
        }
        if (publication.kind === Track.Kind.Audio) {
            createdMediaStream.getAudioTracks().forEach((t) => {
                t.enabled = !publication.isMuted;
            });
        }
    }, [createdMediaStream]);
    // if we've an active mic for room + speech to text on
    // sometime it make confused to user if they would like to mute/unmute
    // so, we'll do the same if a user tries to mute/unmute their room mic
    useEffect(() => {
        currentRoom.localParticipant.on(ParticipantEvent.TrackMuted, handleUserMutedMic);
        currentRoom.localParticipant.on(ParticipantEvent.TrackUnmuted, handleUserMutedMic);
        return () => {
            currentRoom.localParticipant.off(ParticipantEvent.TrackMuted, handleUserMutedMic);
            currentRoom.localParticipant.off(ParticipantEvent.TrackUnmuted, handleUserMutedMic);
        };
    }, [createdMediaStream]);
    const unsetRecognizer = useCallback(() => {
        if (recognizer) {
            try {
                recognizer.stopContinuousRecognitionAsync();
                recognizer.close();
                setRecognizer(undefined);
            }
            catch (e) { }
        }
    }, [recognizer]);
    useEffect(() => {
        let interval = undefined;
        if (recognizer) {
            interval = setInterval(async () => {
                const res = await renewAzureToken();
                if (res && !res.status) {
                    toast(t(res.msg), {
                        type: 'error',
                    });
                }
            }, tokenRenewInterval);
        }
        return () => {
            if (recognizer) {
                unsetRecognizer();
            }
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [recognizer]);
    // we'll update token
    useEffect(() => {
        if (recognizer &&
            azureTokenInfo &&
            azureTokenInfo.renew &&
            azureTokenInfo.token !== '') {
            recognizer.authorizationToken = azureTokenInfo.token;
            dispatch(cleanAzureToken());
        }
    }, [recognizer, azureTokenInfo]);
    useEffect(() => {
        if (isEmpty(deviceId) && !mediaStream) {
            return;
        }
        if (isEmpty(speechLang)) {
            return;
        }
        const getToken = async () => {
            setOptionSelectionDisabled(true);
            const res = await getAzureToken();
            if (!res.status) {
                toast(t(res.msg), {
                    type: 'error',
                });
            }
            setOptionSelectionDisabled(false);
        };
        getToken();
    }, [deviceId, mediaStream, speechLang]);
    useEffect(() => {
        if (isEmpty(deviceId) && !mediaStream) {
            return;
        }
        const startConnection = async () => {
            if (speechService &&
                azureTokenInfo &&
                !azureTokenInfo.renew &&
                !isEmpty(azureTokenInfo.token) &&
                !isEmpty(speechLang)) {
                setOptionSelectionDisabled(true);
                let mStream = mediaStream;
                // we'll create media stream otherwise won't be able to mute audio stream
                if (!isEmpty(deviceId) && !mediaStream) {
                    // use livekit track creation method for simplicity
                    const m = await currentRoom.localParticipant.createTracks({
                        audio: {
                            deviceId,
                        },
                        video: false,
                    });
                    mStream = m[0].mediaStream;
                }
                setCreatedMediaStream(mStream);
                // for the beginning, we'll check if our room mic is muted or not
                if (currentRoom.localParticipant.audioTracks.size) {
                    currentRoom.localParticipant.audioTracks.forEach((t) => {
                        if (t.isMuted && mStream) {
                            mStream.getAudioTracks().forEach((t) => {
                                if (t.enabled) {
                                    t.enabled = false;
                                }
                            });
                        }
                    });
                }
                openConnectionWithAzure(azureTokenInfo, mStream, speechLang, speechService, setOptionSelectionDisabled, setRecognizer, unsetRecognizer);
                dispatch(cleanAzureToken());
            }
        };
        startConnection();
    }, [azureTokenInfo, deviceId, mediaStream, speechLang, speechService]);
    const onCloseSelectedOptions = useCallback((o) => {
        dispatch(updateSelectedSubtitleLang(o.subtitleLang));
        if (!isEmpty(o.speechLang)) {
            setSpeechLang(`${o.speechLang}`);
        }
        if (!isEmpty(o.micDevice)) {
            setDeviceId(o.micDevice);
            setMediaStream(undefined);
            setCreatedMediaStream(undefined);
        }
        else {
            currentRoom.localParticipant.audioTracks.forEach((publication) => {
                if (publication.track &&
                    publication.track.source === Track.Source.Microphone &&
                    publication.track.mediaStream) {
                    setMediaStream(publication.track.mediaStream);
                }
            });
        }
        if (o.stopService && recognizer) {
            unsetRecognizer();
            setSpeechLang('');
            setDeviceId('');
            setMediaStream(undefined);
            setCreatedMediaStream(undefined);
        }
    }, 
    //eslint-disable-next-line
    [currentRoom.localParticipant.audioTracks, recognizer]);
    const onOpenSelectedOptionsModal = () => {
        setSpeechLang('');
        setDeviceId('');
        setMediaStream(undefined);
        setCreatedMediaStream(undefined);
    };
    return (_jsx(_Fragment, { children: speechService ? (_jsxs("div", { className: "speechService absolute bottom-0 w-full z-50 left-0", children: [_jsxs("div", { className: "wrap", children: [_jsx(SelectOptions, { optionSelectionDisabled: optionSelectionDisabled, speechService: speechService, recognizer: recognizer, onCloseSelectedOptions: onCloseSelectedOptions, onOpenSelectedOptionsModal: onOpenSelectedOptionsModal }), _jsx(SubtitleTextsHistory, { isOpenPopover: setIsOpenPopover })] }), !isOpenPopover ? _jsx(SubtitleArea, {}) : null] })) : null }));
};
_react_refresh_temp_3 = SpeechToTextService;
$RefreshReg$(_react_refresh_temp_3, "SpeechToTextService");
_react_refresh_temp_4(SpeechToTextService, "cDF8EjFMGt7Fs6HtU78JlfvUz2A=", false, () => [useTranslation, useAppDispatch, useAppSelector, useAppSelector]);
export default SpeechToTextService;
